<template>
  <v-navigation-drawer
    ref="drawerRef"
    v-model="devShowSettings"
    class="dev-settings-drawer"
    disable-route-watcher
    location="right"
    temporary
    touchless
    :width="drawerWidth"
  >
    <v-toolbar flat height="52">
      <v-app-bar-nav-icon v-if="smAndUp">
        <v-btn
          :icon="isDefaultWidth ? 'far fa-angles-left' : 'far fa-angles-right'"
          @click="onSetToggleWidth"
        />
      </v-app-bar-nav-icon>
      <v-spacer v-if="!isDefaultWidth" />
      <v-toolbar-title>
        <span class="text-title-large">DeepBox DEV Settings</span>
      </v-toolbar-title>
      <v-spacer v-if="!isDefaultWidth" />
      <v-btn icon="$close" @click="devShowSettings = false" />
    </v-toolbar>

    <v-defaults-provider
      :defaults="{
        VSwitch: {
          hideDetails: true,
          inset: true,
          size: 'small',
          falseIcon: '$close',
          color: 'success',
          density: 'compact',
          label: 'Debug Enabled',
          trueIon: 'far fa-check',
        },
      }"
    >
      <v-card-text>
        <v-switch
          v-model="settingsDevStore.devModeEnabled"
          label="DevMode Enabled"
        />
        <span class="text-body-small">Enable or disable the dev mode</span>

        <v-divider class="ma-4" />

        <v-form :disabled="!settingsDevStore.devModeEnabled">
          <div v-for="feature in featuresItems" :key="feature.id">
            <v-switch
              v-model="settingsDevStore.devModeFeatures"
              :label="feature.text"
              multiple
              :value="feature.value"
            >
              <template #label>
                <div class="flex-grow-1">
                  {{ feature.text }}
                </div>

                <DeepStageLabel
                  v-if="feature.flag"
                  class="ml-1"
                  :flag="feature.flag"
                  icon-display-as-fallback="chip"
                  size="x-small"
                />
              </template>
            </v-switch>
            <span v-if="feature.description" class="text-body-small">
              {{ feature.description }}
            </span>
            <v-form
              v-if="feature.settings && feature.settings.length > 0"
              class="pl-8"
              :disabled="
                !settingsDevStore.devModeFeatures.includes(feature.value) ||
                !settingsDevStore.devModeEnabled
              "
            >
              <template
                v-for="featureSetting in feature.settings"
                :key="featureSetting.id"
              >
                <v-checkbox
                  color="primary"
                  density="compact"
                  hide-details
                  :label="featureSetting.text"
                  :model-value="featureSetting.value"
                  @update:model-value="featureSetting.click"
                />
                <span v-if="featureSetting.description" class="text-body-small">
                  {{ featureSetting.description }}
                </span>
              </template>
            </v-form>
          </div>
          <v-switch
            v-model="settingsDevStore.devDebugEnabled"
            label="Debug Enabled"
          />
          <span class="text-body-small">Enables debug infos on UI</span>
          <v-switch
            class="me-auto"
            false-icon="far fa-sun-bright"
            :label="`Theme: ${isDarkTheme ? 'dark' : 'light'}`"
            :model-value="isDarkTheme"
            true-icon="far fa-moon"
            @update:model-value="toggleLightDarkTheme()"
          />
          <span class="text-body-small">Switch theme to dark/light</span>
          <template v-if="isLocalEnv()">
            <v-divider class="ma-4" />
            <div class="text-body-medium">Theme</div>
            <v-list-subheader>Local only</v-list-subheader>
            <div class="d-flex">
              <div class="d-flex">
                <v-menu :close-on-content-click="false" z-index="2501">
                  <template #activator="{ props: ActivatorProps }">
                    <v-btn color="primary" v-bind="ActivatorProps">
                      Primary Color
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-text>
                      <v-color-picker
                        :model-value="
                          theme.current.value.colors.primary as string
                        "
                        @update:model-value="
                          (v: string) =>
                            (theme.themes.value[
                              theme.global.name.value
                            ].colors.primary = v)
                        "
                      />
                    </v-card-text>
                  </v-card>
                </v-menu>
                {{
                  theme.themes.value[theme.global.name.value].colors[
                    'deepbox-primary'
                  ]
                }}
              </div>
            </div>
          </template>
        </v-form>
      </v-card-text>
    </v-defaults-provider>

    <template #append>
      <v-card-text>
        <v-divider />
        <div
          v-for="releaseInfo in releaseInfos"
          :key="releaseInfo.title"
          class="text-medium-emphasis text-body-small py-2 px-3 d-flex align-center"
        >
          <div class="d-inline-flex align-center">
            <v-icon size="small" start>{{ releaseInfo.icon }}</v-icon>
            {{ releaseInfo.title }}
          </div>
          <v-btn
            v-if="releaseInfo.value"
            class="text-none px-2 ms-auto"
            color="primary"
            density="compact"
            :loading="copied && text === releaseInfo.value"
            rounded
            variant="text"
            @click="
              isSupported
                ? copy(
                    releaseInfo.valueToCopy
                      ? releaseInfo.valueToCopy
                      : releaseInfo.value,
                  )
                : undefined
            "
          >
            {{ releaseInfo.value }}
          </v-btn>
        </div>
      </v-card-text>
    </template>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { toast } from 'vue-sonner'
import { useDisplay, useTheme } from 'vuetify'
import { isLocalEnv } from '@/utils/helpers/env.ts'
import { isDeepAEnabled } from '@/utils/helpers/features.ts'
import { DeepStageLabel, useAppTheme } from '@deepcloud/deep-ui-lib'
import { formatDateTimeByDefaultFormat } from '@deepcloud/deep-ui-utils/date'
import { useSettingsDevStore } from '@/stores/settings/settings-dev'
import { DeviceKey } from '@/plugins/device-detector-js.ts'
import { useKeycloak } from '@/composables/auth/keycloak'
import type { DevModeFeatures } from '@/components/dev/const.ts'
import type { Flags } from '@deepcloud/deep-ui-lib'

const { isDarkTheme, toggleLightDarkTheme } = useAppTheme()
const theme = useTheme()
const { t } = useI18n()

const settingsDevStore = useSettingsDevStore()
const { devShowSettings } = storeToRefs(settingsDevStore)

// always close drawer on load
devShowSettings.value = false

const DRAWER_DEFAULT_WIDTH = 400
const drawerWidth = ref(DRAWER_DEFAULT_WIDTH)

const isDefaultWidth = computed(
  () => drawerWidth.value === DRAWER_DEFAULT_WIDTH,
)

function resetDrawerWidth() {
  drawerWidth.value = DRAWER_DEFAULT_WIDTH
}

function onSetToggleWidth() {
  if (isDefaultWidth.value) {
    drawerWidth.value = window.innerWidth
  } else {
    resetDrawerWidth()
  }
}

const { xs, smAndUp } = useDisplay()
watch(
  () => devShowSettings.value,
  (newValue) => {
    if (newValue) {
      if (xs.value) {
        drawerWidth.value = window.innerWidth
      } else {
        resetDrawerWidth()
      }
    } else {
      resetDrawerWidth()
    }
  },
)

const doActiveApiPolling = useLocalStorage('activeDeepFlowTaskPolling', false)

const { bearer } = useKeycloak()

const featuresItems = computed(() =>
  [
    {
      id: 'DEEP_FLOW',
      text: 'DeepFlow',
      value: 'DEEP_FLOW' as DevModeFeatures,
      flag: 'DEV' as Flags,
      description:
        'Enable DeepFlow Tasks Menu on AppBar and option on files context menu inside the box view',
      settings: [
        {
          id: 'test-1',
          text: 'API Task polling',
          value: doActiveApiPolling.value,
          description: 'Enables active tasks polling from the API',
          click: () => {
            doActiveApiPolling.value = !doActiveApiPolling.value
          },
        },
      ],
    },
    {
      id: 'DEEP_A',
      text: 'DeepA',
      value: 'DEEP_A' as DevModeFeatures,
      flag: 'DEV' as Flags,
      description: 'Enables DeepA Features',
      show: () => isDeepAEnabled(),
    },
    {
      id: 'NEWS_LIST',
      text: 'News list',
      value: 'NEWS_LIST' as DevModeFeatures,
      flag: 'DEV' as Flags,
      description: 'Enables option on the user menu to see all news',
    },
    {
      id: 'BOX_VIEW_OPTION_HIDDEN_NODES',
      text: 'Hidden Nodes',
      value: 'BOX_VIEW_OPTION_HIDDEN_NODES' as DevModeFeatures,
      flag: 'DEV' as Flags,
      description:
        'Enables in box view the option to always show option to set visibility of hidden nodes',
    },
    {
      id: 'UPLOAD_QR_CODE',
      text: 'Upload QrCode',
      value: 'UPLOAD_QR_CODE' as DevModeFeatures,
      flag: 'DEV' as Flags,
      description: 'Enables the QR code scan-to-upload action',
    },
  ].filter((f) => (f.show ? f.show() : true)),
)

const releaseInfos = computed(() => [
  {
    title: 'Environment',
    icon: 'far fa-code',
    value: import.meta.env.VITE_ENVIRONMENT,
  },
  {
    title: 'Tag',
    icon: 'far fa-tag',
    value: import.meta.env.VITE_RELEASE_TAG,
  },
  {
    title: 'Datetime',
    icon: 'far fa-clock',
    value: formatDateTimeByDefaultFormat(import.meta.env.VITE_RELEASE_DATETIME),
  },
  {
    title: 'Commit ID',
    icon: 'far fa-code-commit',
    value: import.meta.env.VITE_RELEASE_COMMIT_ID,
  },
  {
    title: 'Auth Token',
    icon: 'far fa-coin',
    value: bearer.value ? `${bearer.value?.substring(0, 10)}...` : '',
    valueToCopy: bearer.value,
  },
])

const { text, copy, copied, isSupported } = useClipboard()

watch(
  () => copied.value,
  (newValue) => {
    if (newValue) {
      toast.success(t('toast.success.copy_value'))
    }
  },
  { immediate: true },
)

const device = inject(DeviceKey)

function onKeyDown(e: KeyboardEvent) {
  let keyCommandMatch
  if (device?.isMacOS) {
    keyCommandMatch = e.metaKey && e.key === 'd'
  } else {
    keyCommandMatch = e.ctrlKey && e.key === 'd'
  }
  if (keyCommandMatch) {
    devShowSettings.value = !devShowSettings.value
    e.preventDefault()
  }
}

onMounted(() => window.addEventListener('keydown', onKeyDown))
onUnmounted(() => window.removeEventListener('keydown', onKeyDown))
</script>

<style scoped lang="scss">
.dev-settings-drawer {
  // show drawer over dialogs
  // 2500 the nr is the default z-index of VOverlay + 1
  z-index: 2501 !important;
}
</style>
